/* Journey timeline (Mi a HortaBerry?). Vanilla, no dependencies.
   The dotted path is rebuilt from the cards' boxes (the article, which never animates)
   whenever the section changes size. Scrolling only changes the height of a nested svg (a
   plain viewport clip, no url(#id) references) and moves the tip: one rect read per frame,
   and nothing runs while the section is off screen. Scroll is heard from any scroller
   (window or a theme wrapper), not just the window.
   Debug: in the browser console, document.querySelector('[data-jr]').jrDiagnose()
   Works from the stages' real order, never DOM position (the platform may wrap blocks). */
(() => {
  // Run once: the script tag is included by every journey section, and a second copy would set
  // each section up again (a second line layer whose timeline it then removed: a frozen line).
  if (window.__hbJourneyLoaded) return;
  window.__hbJourneyLoaded = true;
  const MAG = [2.2, 1.7, 2.0]; // must match _jr-stage.liquid
  const ROMAN = 'I II III IV V VI VII VIII IX X XI XII XIII XIV XV XVI XVII XVIII XIX XX'.split(' ');
  const SAMPLE = 6; // px between stored path points
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();

  const inPageTransition = () => {
    try { return document.documentElement.matches(':active-view-transition'); } catch (e) { return false; }
  };
  // Savor slides the page in on navigation; positions measured or entrances played during the
  // slide are wrong or hidden. Wait for it (at most 2.5 s).
  function afterPageTransition(fn) {
    const t0 = performance.now();
    const tick = () => (inPageTransition() && performance.now() - t0 < 2500 ? requestAnimationFrame(tick) : fn());
    requestAnimationFrame(tick);
  }

  /* Words stay whole: a heading whose longest word is wider than its line shrinks in 5% steps
     (down to 50%) until every word fits. Breaking after a hyphen stays allowed (a normal line
     break); breaking inside a word does not (the CSS sets that). Font size, so lines reflow. */
  function fitWholeWords(els) {
    els.forEach(function (el) {
      el.style.fontSize = '';
      if (!el.clientWidth || el.scrollWidth <= el.clientWidth + 1) return;
      var base = parseFloat(getComputedStyle(el).fontSize) || 16;
      for (var k = 0.95; k >= 0.5; k -= 0.05) {
        el.style.fontSize = (base * k).toFixed(1) + 'px';
        if (el.scrollWidth <= el.clientWidth + 1) break;
      }
    });
  }
  function watchWholeWords(getEls) {
    var run = function () { fitWholeWords(getEls()); };
    run();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    var lastW = window.innerWidth, t;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastW) return; // width changes only (a phone's bars change the height)
      lastW = window.innerWidth; clearTimeout(t); t = setTimeout(run, 150);
    });
  }

  function mount(root) {
    if (instances.has(root)) return;
    watchWholeWords(function () { return [...root.querySelectorAll('.jr-stage :is(h1, h2, h3, h4)')]; });
    const flow = root.querySelector('[data-jr-flow]');
    if (!flow) return;
    const svg = flow.querySelector('[data-jr-svg]');
    const paths = svg ? [...svg.querySelectorAll('[data-jr-path]')] : [];
    const revealSvg = svg && svg.querySelector('[data-jr-reveal]');
    const dots = svg && svg.querySelector('.jr__dots');
    const seed = svg && svg.querySelector('[data-jr-seed]');
    const spark = svg && svg.querySelector('[data-jr-spark]');
    /* scroll | static | off. Touch screens can use their own choice ("Line on touch screens"):
       on iPhone the drawing line and berry jitter while scrolling (iOS 26, Oct 2026), a fully
       drawn line can't. */
    const touch = matchMedia('(hover: none) and (pointer: coarse)').matches;
    const mode = (touch && root.dataset.line === 'scroll' && root.dataset.lineTouch === 'static') ? 'static' : root.dataset.line;
    const animate = root.dataset.motion !== 'none' && 'IntersectionObserver' in window;
    let cards = [], nodeY = [], lit = [], xs = new Float32Array(0), ys = new Float32Array(0);
    let gaps = []; // [from y, to y] of each visible stretch: line start → node, card foot → next node
    let visible = false, frame = 0, buildFrame = 0, sparkOn = false, drawnH = -1, lastReveal = 0, why = 'not built yet';
    const off = [];

    const itemOf = (el) => { while (el.parentElement && el.parentElement !== flow) el = el.parentElement; return el; };
    const prevItem = (el) => { do { el = el.previousElementSibling; } while (el && el.matches('style, script, svg')); return el; };
    // Boxes inside the flow. Only untransformed elements are measured this way: the article
    // (the entrance and tilt move the paper inside it) and the introduction blocks.
    const boxIn = (el, fr) => { const r = el.getBoundingClientRect(); return { x: r.left - fr.left, y: r.top - fr.top, w: r.width, h: r.height }; };

    // Liquid resolves "Automatic" settings from block order; re-apply from the real DOM order
    // so an editor drag (which can reorder without a re-render) can't break the alternation.
    function arrange() {
      cards = [...root.querySelectorAll('[data-jr-stage]')];
      const numStyle = root.dataset.num;
      cards.forEach((c, i) => {
        const right = i % 2 === 1;
        c.classList.toggle('jr-stage--first', i === 0);
        if (c.dataset.autoPos) {
          c.classList.remove('jr-stage--pos-left', 'jr-stage--pos-center', 'jr-stage--pos-right');
          c.classList.add(right ? 'jr-stage--pos-right' : 'jr-stage--pos-left');
        }
        const pos = c.classList.contains('jr-stage--pos-right') ? 'right' : c.classList.contains('jr-stage--pos-center') ? 'center' : 'left';
        if (c.dataset.autoArt && !c.classList.contains('jr-stage--no-art')) {
          c.classList.remove('jr-stage--art-left', 'jr-stage--art-right');
          c.classList.add(pos === 'right' ? 'jr-stage--art-left' : 'jr-stage--art-right');
        }
        if (c.dataset.autoLean) c.style.setProperty('--jr-tilt', (right ? '' : '-') + MAG[i % 3] + 'deg');
        if (c.dataset.autoNode) c.style.setProperty('--jr-node-x', (pos === 'right' ? 26 : pos === 'center' ? 50 : 74) + '%');
        const num = c.dataset.autoNum && c.querySelector('[data-jr-num]');
        if (num) num.textContent = numStyle === 'arabic' ? i + 1 + '.' : numStyle === 'roman' ? (ROMAN[i] || i + 1) + '.' : '';
      });
      lit = cards.map(() => null);
    }

    function build() {
      buildFrame = 0;
      if (!svg || mode === 'off') return update();
      // All reads first, all writes after: no forced style or layout passes in between.
      const cs = getComputedStyle(root);
      const tiltK = parseFloat(cs.getPropertyValue('--jr-tilt-k')) || 0;
      const dot = parseFloat(cs.getPropertyValue('--jr-dot')) || 1.5;
      const fr = flow.getBoundingClientRect();
      const gap = parseFloat(cs.getPropertyValue('--jr-dot-gap')) || 8;
      const pts = []; // [node, foot, card index] per laid-out card, in flow coordinates
      for (let ci = 0; ci < cards.length; ci++) {
        const c = cards[ci];
        const paper = c.querySelector('[data-jr-paper]');
        if (!paper || !paper.offsetWidth) continue;
        // Paper box = article box + the paper's layout offset inside it (offsetParent is the
        // article, which is positioned). Neither includes the paper's transforms.
        const ab = boxIn(c, fr);
        const o = paper.offsetParent === c ? { x: ab.x + paper.offsetLeft, y: ab.y + paper.offsetTop } : { x: ab.x, y: ab.y };
        const w = paper.offsetWidth, h = paper.offsetHeight;
        const f = (parseFloat(c.style.getPropertyValue('--jr-node-x')) || 50) / 100;
        // The paper is rotated about its centre: rotate the node and foot with it, using the
        // settled angle, so the line meets the marker exactly once the card has landed.
        const a = (parseFloat(c.style.getPropertyValue('--jr-tilt')) || 0) * tiltK * Math.PI / 180;
        const cx = o.x + w / 2, cy = o.y + h / 2, cos = Math.cos(a), sin = Math.sin(a);
        const rot = (x, y) => ({ x: cx + (x - cx) * cos - (y - cy) * sin, y: cy + (x - cx) * sin + (y - cy) * cos });
        pts.push([rot(o.x + w * f, o.y), rot(o.x + w * f, o.y + h), ci]);
      }
      if (!pts.length) {
        why = cards.length ? 'stages found but none has a size (hidden?)' : 'no stages in this section';
        if (cards.length) console.warn('[HortaBerry journey] line not drawn:', why, root);
        paths.forEach((p) => p.removeAttribute('d')); xs = ys = new Float32Array(0); nodeY = []; return update();
      }

      // Start just below whatever comes before the first stage (the introduction).
      const before = prevItem(itemOf(cards[pts[0][2]]));
      const bo = before && before.offsetHeight ? boxIn(before, fr) : null;
      let x, y;
      if (bo) {
        x = bo.x + bo.w / 2;
        y = bo.y + bo.h + Math.max(0, Math.min(48, (pts[0][0].y - bo.y - bo.h) * .25));
      } else { x = pts[0][0].x; y = pts[0][0].y - 60; }
      seed.setAttribute('cx', x); seed.setAttribute('cy', y);
      seed.setAttribute('r', dot * 1.6);
      // The tip glow scales with the dots, so a bolder line gets a matching tip.
      spark?.querySelector('.jr__spark-core')?.setAttribute('r', (dot * 1.5).toFixed(1));
      spark?.querySelector('.jr__spark-halo')?.setAttribute('r', (dot * 4.5).toFixed(1));

      const wander = Math.max(0, Math.min(1, parseFloat(root.dataset.wander) || 0));
      const reach = Math.min(160, flow.clientWidth * .12) * wander;
      let d = `M${x.toFixed(1)} ${y.toFixed(1)}`;
      const r1 = (v) => Math.round(v * 10) / 10; // the path text's rounding, so both describe the same line
      const segs = []; let px = r1(x), py = r1(y);
      nodeY = []; gaps = [];
      pts.forEach(([n, foot, ci], i) => {
        gaps.push([y, n.y]);
        const dy = Math.max(1, n.y - y);
        // Control points split the drop evenly (0.5 / 0.5), so y only ever increases along the
        // path: this is what lets the reveal be a simple horizontal clip edge.
        const drift = (n.x >= x ? -1 : 1) * reach;
        d += ` C${(x + drift).toFixed(1)} ${(y + dy * .5).toFixed(1)} ${(n.x - drift).toFixed(1)} ${(n.y - dy * .5).toFixed(1)} ${n.x.toFixed(1)} ${n.y.toFixed(1)}`;
        segs.push([px, py, r1(x + drift), r1(y + dy * .5), r1(n.x - drift), r1(n.y - dy * .5), r1(n.x), r1(n.y)]); px = r1(n.x); py = r1(n.y);
        nodeY[ci] = n.y;
        if (i < pts.length - 1) { // thread behind the card and leave from its foot
          x = foot.x; y = Math.max(foot.y - 14, n.y + 1);
          d += ` L${x.toFixed(1)} ${y.toFixed(1)}`;
          segs.push([px, py, r1(x), r1(y)]); px = r1(x); py = r1(y);
        }
      });
      paths.forEach((p) => {
        p.setAttribute('d', d);
        // Short real dashes with round caps: dots in every engine (zero-length dashes and
        // mixed-unit CSS dash lists are not reliable everywhere).
        p.setAttribute('stroke-dasharray', `0.01 ${gap}`);
      });
      if (revealSvg) revealSvg.setAttribute('width', Math.ceil(fr.width) + 4000);
      why = 'ok';
      /* Points every SAMPLE px along the path, computed from its own segments: getPointAtLength
         walks the path from its start for every point (~1000 calls, ~0.7-1 s of a phone's
         main thread per page load). Each segment is cut into ~2 px pieces; the points are
         taken along those, at the same spacing as before. */
      const fx = [segs.length ? segs[0][0] : x], fy = [segs.length ? segs[0][1] : y], fl = [0];
      for (const s of segs) {
        if (s.length === 8) {
          const [ax, ay, b1x, b1y, b2x, b2y, cx, cy] = s;
          const chord = Math.hypot(cx - ax, cy - ay) + Math.hypot(b1x - ax, b1y - ay) + Math.hypot(cx - b2x, cy - b2y);
          const steps = Math.max(8, Math.ceil(chord / 2));
          for (let k = 1; k <= steps; k++) {
            const u = k / steps, v = 1 - u;
            const qx = v * v * v * ax + 3 * v * v * u * b1x + 3 * v * u * u * b2x + u * u * u * cx;
            const qy = v * v * v * ay + 3 * v * v * u * b1y + 3 * v * u * u * b2y + u * u * u * cy;
            fl.push(fl[fl.length - 1] + Math.hypot(qx - fx[fx.length - 1], qy - fy[fy.length - 1])); fx.push(qx); fy.push(qy);
          }
        } else {
          const cx = s[2], cy = s[3];
          fl.push(fl[fl.length - 1] + Math.hypot(cx - fx[fx.length - 1], cy - fy[fy.length - 1])); fx.push(cx); fy.push(cy);
        }
      }
      const len = fl[fl.length - 1], n = Math.max(2, Math.ceil(len / SAMPLE) + 1);
      xs = new Float32Array(n); ys = new Float32Array(n);
      for (let i = 0, j = 1; i < n; i++) {
        const at = Math.min(len, i * SAMPLE);
        while (j < fl.length - 1 && fl[j] < at) j++;
        const f = (at - fl[j - 1]) / ((fl[j] - fl[j - 1]) || 1);
        xs[i] = fx[j - 1] + (fx[j] - fx[j - 1]) * f; ys[i] = fy[j - 1] + (fy[j] - fy[j - 1]) * f;
      }
      drawnH = -1;
      sdBuild(fr);
      update();
    }

    // Line speed. Across each gap the tip covers the whole stretch in 1/speed of the scroll,
    // then rests at the next card's marker until the reading line catches up, and sets off
    // again from that card's foot. Per gap, so every card gets the same rhythm and the tip
    // never drifts further ahead down the page. Inside a card the tip is hidden behind it.
    const speed = Math.max(1, parseFloat(root.dataset.speed) || 1);

    /* Scroll-driven line and spark. The script used to redraw both on every scroll frame, one
       frame behind the page, so on 90-120 Hz phones the spark trailed and stepped. Where the
       browser supports scroll-driven animations, they run on its scrolling thread instead:
       the line through a moving-window clip (an outer box sliding down while its contents
       slide up by the same amount, so only layers move) and the spark as an HTML element on
       the sampled path. The keyframes reproduce update() exactly: the same reading line, the
       same speed across each gap, the same rest at each marker (spark hidden). */
    const sdOk = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: --jr-a') && CSS.supports('animation-range: 1px 2px'));
    let sdActive = false, sdLayer = null, sdStyle = null;
    const uid = 'jr' + Math.random().toString(36).slice(2, 8);
    function scrollerOf(el) { // the element that actually scrolls this page (Savor sometimes scrolls an inner wrapper)
      for (let n = el.parentElement; n && n !== document.documentElement; n = n.parentElement) {
        const o = getComputedStyle(n).overflowY;
        if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
      }
      return document.scrollingElement || document.documentElement;
    }
    let sdScroller = null, sdName = '';
    let probe = null;
    function lvh() { // the large viewport height: stable while a phone's toolbar slides in and out
      if (!probe) { probe = document.createElement('div'); probe.style.cssText = 'position:fixed;top:0;left:-9999px;width:1px;height:100vh;height:100lvh;visibility:hidden;pointer-events:none'; document.body.appendChild(probe); }
      return probe.offsetHeight || innerHeight;
    }
    function sdClear() {
      if (sdScroller) { // take only this section's timeline off the scroller
        const rest = (sdScroller.style.getPropertyValue('scroll-timeline') || '').split(',').map((s) => s.trim()).filter((s) => s && !s.startsWith(sdName));
        if (rest.length) sdScroller.style.setProperty('scroll-timeline', rest.join(', ')); else sdScroller.style.removeProperty('scroll-timeline');
        sdScroller = null;
      }
      if (sdLayer) sdLayer.remove(); if (sdStyle) sdStyle.remove();
      sdLayer = sdStyle = null; sdActive = false; root.classList.remove('jr--sd');
    }
    function sdBuild(fr) {
      const oldLayer = sdLayer, oldStyle = sdStyle;   // swapped out only once the new layer is in
      sdLayer = sdStyle = null;
      const done = () => { if (oldLayer) oldLayer.remove(); if (oldStyle) oldStyle.remove(); };
      if (!sdOk || mode !== 'scroll' || reduce.matches || ys.length < 2 || !gaps.length) { done(); sdClear(); return; }
      const sc = scrollerOf(root), isRoot = sc === document.scrollingElement || sc === document.documentElement;
      const scTop = isRoot ? 0 : sc.getBoundingClientRect().top;
      const flowTop = fr.top - scTop + sc.scrollTop;               // the flow's top in the scroller's scroll coordinates
      const rv = parseFloat(root.dataset.reveal) || .6, H = lvh();
      const sOf = (L) => L - H * rv + flowTop;                      // scroll offset at which update()'s reading line = L
      const T = Math.ceil(fr.height), last = ys.length - 1;
      const L0 = ys[0] - 2, L1 = ys[last] + 2;
      // reveal(line), piecewise linear: (line, reveal) points, exactly like tipFor()
      const pts = [[L0, L0]];
      for (const [a, b] of gaps) { if (b > a) { const m = a + (b - a) / speed; pts.push([a, a], [m, b], [b, b]); } }
      pts.push([L1, L1]);
      const seq = []; for (const q of pts) if (!seq.length || q[0] > seq[seq.length - 1][0] + 0.01) seq.push(q);
      const span = seq[seq.length - 1][0] - seq[0][0] || 1, pct = (L) => ((L - L0) / span * 100).toFixed(4) + '%';
      let css = `@keyframes ${uid}-o{` + seq.map(([L, R]) => `${pct(L)}{translate:0 ${(R - T).toFixed(1)}px}`).join('') + '}'
              + `@keyframes ${uid}-i{` + seq.map(([L, R]) => `${pct(L)}{translate:0 ${(T - R).toFixed(1)}px}`).join('') + '}';
      // spark: on the path at the tip; hidden while resting at a marker and outside the line
      const xAt = (y) => { let lo = 0, hi = last; while (lo < hi) { const m = (lo + hi) >> 1; ys[m] < y ? (lo = m + 1) : (hi = m); }
        if (lo === 0) return xs[0]; const t = (y - ys[lo - 1]) / (ys[lo] - ys[lo - 1] || 1); return xs[lo - 1] + (xs[lo] - xs[lo - 1]) * t; };
      const lineFor = (R) => { for (const [a, b] of gaps) if (R >= a && R <= b && b > a) return a + (R - a) / speed; return R; };
      const showSpark = !!spark && root.dataset.spark === '1';
      if (showSpark) {
        const step = Math.max(1, Math.ceil(ys.length / 240)), F = 14, ks = [];
        ks.push([L0, xs[0], ys[0], 0]);
        for (let i = 0; i <= last; i += step) ks.push([lineFor(ys[i]), xs[i], ys[i], 1]);
        ks.push([lineFor(ys[last]), xs[last], ys[last], 1], [L1, xs[last], ys[last], 0]);
        for (const [a, b] of gaps) if (b > a) { // rest at the marker: fade out, wait, fade back in at the card's foot
          const m = a + (b - a) / speed, xb = xAt(b);
          ks.push([m, xb, b, 1], [Math.min(b, m + F), xb, b, 0], [Math.max(m + F, b - 0.5), xb, b, 0], [b, xb, b, 1]);
        }
        ks.sort((p, q) => p[0] - q[0]);
        const kk = []; for (const k of ks) if (!kk.length || k[0] > kk[kk.length - 1][0] + 0.01) kk.push(k); else kk[kk.length - 1] = k;
        css += `@keyframes ${uid}-s{` + kk.map(([L, x, y, o]) => `${pct(Math.max(L0, Math.min(L1, L)))}{translate:${x.toFixed(1)}px ${y.toFixed(1)}px;opacity:${o}}`).join('') + '}';
      }
      // the layer: same place and units as the SVG (flow coordinates, 1 unit = 1 px)
      const tl = `--${uid}`;
      // Add this section's timeline to any others on the same scroller (a second journey section
      // must not overwrite the first one's name), and remember where, to remove only ours.
      const list = (sc.style.getPropertyValue('scroll-timeline') || '').split(',').map((s) => s.trim()).filter((s) => s && !s.startsWith(tl));
      list.push(`${tl} block`);
      sc.style.setProperty('scroll-timeline', list.join(', '));
      sdScroller = sc; sdName = tl;
      const range = `${Math.round(sOf(seq[0][0]))}px ${Math.round(sOf(seq[seq.length - 1][0]))}px`;
      const anim = (name) => `animation:${name} linear both;animation-timeline:${tl};animation-range:${range};`;
      sdStyle = document.createElement('style');
      sdStyle.textContent = css + `.${uid}-o{${anim(uid + '-o')}}.${uid}-i{${anim(uid + '-i')}}.${uid}-s{${anim(uid + '-s')}}`;
      document.head.appendChild(sdStyle);
      const dots0 = svg.querySelector('.jr__dots');
      /* The clip window spans only the line's own sideways extent (plus the dots): a window as
         wide as the old SVG allowance (+4000 px) made two ~4400 x 4200 px layers, which phones
         draw tile by tile while they move: the flicker on slow scrolls. */
      let minX = Infinity, maxX = -Infinity;
      for (let i = 0; i <= last; i++) { if (xs[i] < minX) minX = xs[i]; if (xs[i] > maxX) maxX = xs[i]; }
      const dotW = parseFloat(getComputedStyle(root).getPropertyValue('--jr-dot')) || 1.5;
      const pad = Math.ceil(dotW * 3 + 4), left = Math.floor(minX - pad), width = Math.ceil(maxX - minX + 2 * pad);
      const hr = Math.ceil(parseFloat(spark?.querySelector('.jr__spark-halo')?.getAttribute('r')) || 9) + 2; // the berry's own box: its glow
      sdLayer = document.createElement('div');
      sdLayer.className = 'jr__sd'; sdLayer.setAttribute('aria-hidden', 'true');
      sdLayer.innerHTML = `<div class="jr__sd-o ${uid}-o" style="left:${left}px;width:${width}px;height:${T}px"><div class="jr__sd-i ${uid}-i" style="height:${T}px">`
        + `<svg width="${width}" height="${T}"><g transform="translate(${-left} 0)"></g></svg></div></div>`
        + (showSpark ? `<div class="jr__sd-s ${uid}-s" style="width:${2 * hr}px;height:${2 * hr}px;margin:${-hr}px 0 0 ${-hr}px"><svg width="${2 * hr}" height="${2 * hr}" viewBox="${-hr} ${-hr} ${2 * hr} ${2 * hr}">${spark.innerHTML}</svg></div>` : '');
      const g = sdLayer.querySelector('g');
      if (dots0) { const c = dots0.cloneNode(false); c.removeAttribute('data-jr-path'); g.appendChild(c); }
      svg.after(sdLayer);
      done();
      sdActive = true; root.classList.add('jr--sd');
    }
    function tipFor(line) {
      for (const [a, b] of gaps) {
        if (line < a) return line;      // still behind the previous card
        if (line < b) return Math.min(b, a + (line - a) * speed);
      }
      return line;                      // past the last marker
    }

    function update() {
      frame = 0;
      const full = mode !== 'scroll' || reduce.matches;
      const line = full ? Infinity : innerHeight * (parseFloat(root.dataset.reveal) || .6) - flow.getBoundingClientRect().top;
      const reveal = full ? Infinity : tipFor(line);
      const resting = !full && gaps.some(([, b]) => reveal === b && line < b);
      lastReveal = reveal;
      // Writes only below this line.
      if (revealSvg && !sdActive) {
        const h = full ? 1e6 : Math.max(0, Math.round(reveal));
        if (h !== drawnH) { revealSvg.setAttribute('height', h); drawnH = h; }
      }
      const showSpark = !sdActive && !!spark && root.dataset.spark === '1' && !full && !resting && ys.length > 1 && reveal > ys[0] && reveal < ys[ys.length - 1];
      if (showSpark) {
        let lo = 0, hi = ys.length - 1; // first sample at or below the reveal line
        while (lo < hi) { const m = (lo + hi) >> 1; ys[m] < reveal ? (lo = m + 1) : (hi = m); }
        const y0 = ys[lo - 1], t = (reveal - y0) / (ys[lo] - y0 || 1);
        const sx = xs[lo - 1] + (xs[lo] - xs[lo - 1]) * t;
        spark.setAttribute('transform', `translate(${sx.toFixed(1)} ${reveal.toFixed(1)})`);
      }
      if (spark && showSpark !== sparkOn) { spark.classList.toggle('is-on', showSpark); sparkOn = showSpark; }
      for (let i = 0; i < cards.length; i++) {
        const on = mode === 'off' || full || (nodeY[i] !== undefined && reveal >= nodeY[i] - 1);
        if (on !== lit[i]) { cards[i].classList.toggle('is-lit', on); lit[i] = on; }
      }
    }

    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const scheduleBuild = () => { if (!buildFrame) buildFrame = requestAnimationFrame(build); };
    let scrolls = 0;
    const onScroll = (e) => {
      scrolls++;
      // The scroll-driven line follows one scroller. If the page turns out to scroll another
      // one (Savor's wrapper can start scrolling after the line was set up), its timeline never
      // moves: rebuild it on the element that actually scrolls.
      if (sdActive) {
        const t = e && e.target && e.target !== document ? e.target : (document.scrollingElement || document.documentElement);
        if (t !== sdScroller && t.contains && t.contains(root)) scheduleBuild();
      }
      if (visible) schedule();
    };

    arrange();

    // Entrance: each card fades in as it nears the screen.
    let entrance = null;
    if (animate && !reduce.matches) {
      root.classList.add('jr--animate');
      entrance = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('is-near');
          entrance.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    } else {
      cards.forEach((c) => c.classList.add('is-near'));
    }
    root.classList.remove('jr--pre');

    // Only follow the scroll while the section is (nearly) on screen.
    const presence = 'IntersectionObserver' in window
      ? new IntersectionObserver((es) => { visible = es[es.length - 1].isIntersecting; if (visible) schedule(); }, { rootMargin: '25% 0px' })
      : null;
    if (presence) presence.observe(root); else visible = true;

    const ro = 'ResizeObserver' in window ? new ResizeObserver(scheduleBuild) : null;
    if (ro) ro.observe(flow);
    // Savor can scroll inside body / .page-wrapper (overflow: auto) instead of the window, and
    // then the window never gets a scroll event. Element scroll events don't bubble, but a
    // capturing listener on the document sees every one, whichever element is scrolling.
    const scrollOpts = { capture: true, passive: true };
    document.addEventListener('scroll', onScroll, scrollOpts);
    addEventListener('resize', schedule, { passive: true });
    off.push(() => sdClear());
    off.push(() => { if (probe) probe.remove(); });
    off.push(() => document.removeEventListener('scroll', onScroll, scrollOpts), () => removeEventListener('resize', schedule));
    const onMotion = () => { if (reduce.matches) cards.forEach((c) => c.classList.add('is-near')); scheduleBuild(); };
    reduce.addEventListener ? reduce.addEventListener('change', onMotion) : reduce.addListener(onMotion);
    off.push(() => (reduce.removeEventListener ? reduce.removeEventListener('change', onMotion) : reduce.removeListener(onMotion)));

    afterPageTransition(() => {
      if (entrance) cards.forEach((c) => entrance.observe(c));
      build();
    });

    // Console aid: what the line thinks is going on.
    root.jrDiagnose = () => ({
      status: why, mode, stages: cards.length, points: ys.length,
      pathLength: dots && dots.getAttribute('d') ? Math.round(dots.getTotalLength()) : 0,
      firstNodeY: nodeY[0], tipY: Math.round(lastReveal), speed, drawnHeight: revealSvg && revealSvg.getAttribute('height'),
      visible, scrollEventsSeen: scrolls, reducedMotion: reduce.matches, stroke: dots && getComputedStyle(dots).stroke,
      dash: dots && dots.getAttribute('stroke-dasharray'), lit: lit.filter(Boolean).length
    });

    instances.set(root, {
      rearrange() { arrange(); cards.forEach((c) => { if (!entrance) c.classList.add('is-near'); else entrance.observe(c); }); scheduleBuild(); },
      reveal(card) { card.classList.add('is-near'); },
      destroy() {
        if (entrance) entrance.disconnect();
        if (presence) presence.disconnect();
        if (ro) ro.disconnect();
        cancelAnimationFrame(frame); cancelAnimationFrame(buildFrame);
        off.forEach((f) => f());
        delete root.jrDiagnose;
        instances.delete(root);
      }
    });
  }

  // Roots in scope, including scope itself (an event target may be the root, not a wrapper).
  const roots = (scope) => {
    const list = [...(scope.querySelectorAll ? scope.querySelectorAll('[data-jr]') : [])];
    if (scope.matches && scope.matches('[data-jr]')) list.unshift(scope);
    return list;
  };
  const all = (scope = document) => roots(scope).forEach(mount);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => all()) : all();

  // Theme editor
  document.addEventListener('shopify:section:load', (e) => all(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    roots(e.target).forEach((r) => instances.get(r)?.destroy());
  });
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest?.('[data-jr]');
    const inst = root && instances.get(root);
    if (!inst) return;
    inst.rearrange(); // drags can reorder stages without re-running mount
    const card = e.target.closest?.('[data-jr-stage]') || e.target.querySelector?.('[data-jr-stage]');
    if (card) inst.reveal(card);
  });
})();
